/**
 * Desktop navigation (≥1024 px): the same destinations as a left rail.
 * A2 `nav.tsx:177-297` (`Brand` and `Sidebar`).
 *
 * The trainer opens BESIDE the screen here (the docked panel), so its row
 * toggles `overlay=coach` and lights while open, and the screen's row stays
 * lit too. It sits under the four tabs (`D218`): the phone floats it, the
 * rail keeps it a row, because a docked panel has a place to open.
 *
 * "Perfil" is a row like the others and carries how complete the profile
 * is; the gear at the foot is the app's settings and the account.
 */
import {
  "shell.badge_draft" as badgeDraft,
  "shell.badge_unread" as badgeUnread,
  "shell.idle_session" as idleSession,
  "shell.live_rest" as liveRest,
  "shell.live_sets" as liveSets,
  "shell.nav_label" as navLabel,
  "shell.search" as searchLabel,
  "shell.settings" as settingsLabel,
  "shell.train_continue" as trainContinue,
  "shell.train_now" as trainNow,
} from "@repo/i18n";
import { Search, Settings } from "lucide-react";
import { useCallback } from "react";
import { cx } from "../../lib/cx";
import { Icon } from "../../ui/controls";
import { sessionTitle } from "../plan/session-name";
import { estimateMinutes } from "../today/model";
import { useTodaysSession } from "../workout/screens/use-workout-data";
import { useNavBadges } from "./badges";
import { useLiveSession } from "./live-session";
import {
  COACH_TAB,
  searchShortcut,
  TABS,
  type Tab,
  useNavigation,
} from "./navigation";
import { PlayGlyph } from "./play-glyph";
import { useProfileCompleteness } from "./use-profile-completeness";

const BRAND_NAME = "My Pocket Gym";

export function Brand() {
  return (
    <div className="flex items-center gap-3">
      {/* The app's own mark — the launcher icon and the favicon — never a
          stand-in glyph: A2's pulse line was a placeholder for it. */}
      <img
        alt=""
        className="size-10 shrink-0 rounded-[10px]"
        height={40}
        src="/favicon.svg"
        width={40}
      />
      <span className="a-display text-lg leading-tight">{BRAND_NAME}</span>
    </div>
  );
}

function Pill({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-accent-soft px-2.5 py-1 font-semibold text-accent-ink text-xs leading-none">
      {children}
    </span>
  );
}

function NavRow({ tab }: { tab: Tab }) {
  const { screen, coachOpen, go, toggleCoach } = useNavigation();
  const badges = useNavBadges();
  const complete = useProfileCompleteness();
  const trainer = tab.id === "coach";
  const active = trainer ? coachOpen : screen === tab.id;
  const onClick = useCallback(() => {
    if (trainer) {
      toggleCoach();
    } else {
      go(tab.id);
    }
  }, [trainer, toggleCoach, go, tab.id]);
  return (
    <button
      aria-current={active && !trainer ? "page" : undefined}
      aria-expanded={trainer ? active : undefined}
      className={cx(
        "flex h-12 items-center gap-3 rounded-full px-4 text-left text-base transition-colors",
        active
          ? "bg-surface font-semibold text-ink shadow"
          : "font-medium text-ink-2 hover:bg-surface-2"
      )}
      // The docked trainer closes on a press outside it (`D220`); its own
      // toggle is not "outside", or it would close and reopen.
      data-coach-toggle={trainer ? "" : undefined}
      onClick={onClick}
      type="button"
    >
      <Icon
        className={active ? "text-accent" : "text-ink-3"}
        icon={tab.icon}
        size={20}
      />
      <span className="flex-1">{tab.label()}</span>
      {tab.id === "plan" && badges.planDraft ? (
        <Pill>{badgeDraft()}</Pill>
      ) : null}
      {trainer && badges.trainerUnread ? <Pill>{badgeUnread()}</Pill> : null}
      {tab.id === "profile" && complete !== null && complete.pct < 100 ? (
        <Pill>{`${complete.pct}%`}</Pill>
      ) : null}
    </button>
  );
}

/**
 * What the Treinar button will start, under it, while nothing is running —
 * "Treino B · 45 min" (A2 `nav.tsx:235-239`). The same reading Today's hero
 * makes — `useTodaysSession` and `estimateMinutes` — so the two never name a
 * different workout. No minutes means no honest estimate exists, and then the
 * title stands alone rather than a made-up total.
 */
function IdleLine() {
  const today = useTodaysSession();
  if (today.status !== "ready") {
    return null;
  }
  const title = sessionTitle(today.session.label);
  const minutes = estimateMinutes(today.session);
  return (
    <p className="mt-3 truncate text-center text-ink-2 text-sm">
      {minutes === null ? title : idleSession({ minutes, title })}
    </p>
  );
}

export function Sidebar() {
  const { openSearch, openSettings, train } = useNavigation();
  const live = useLiveSession();
  const isLive = live !== null;
  const onTrain = useCallback(() => train(isLive), [train, isLive]);
  return (
    <aside
      className="hidden h-full w-68 shrink-0 flex-col border-line border-r px-5 py-7 lg:flex"
      data-sidebar
    >
      <Brand />
      <button
        className="mt-7 flex h-12 items-center gap-2.5 rounded-full border border-line bg-surface pr-2 pl-4 text-left text-ink-3 transition-colors hover:border-line-strong"
        onClick={openSearch}
        type="button"
      >
        <Icon icon={Search} size={18} />
        <span className="flex-1 text-base">{searchLabel()}</span>
        <kbd className="rounded-full bg-surface-2 px-2.5 py-1 font-semibold font-ui text-ink-2 text-xs leading-none">
          {searchShortcut()}
        </kbd>
      </button>
      <button
        className="a-press a-primary mt-4 flex h-14 items-center justify-center gap-2 rounded-full font-bold text-lg"
        onClick={onTrain}
        type="button"
      >
        <PlayGlyph className="size-5" />
        {live ? trainContinue() : trainNow()}
      </button>
      {live ? (
        <p className="mt-3 flex items-center justify-center gap-1.5 text-ink-2 text-sm tabular-nums">
          <span
            aria-hidden="true"
            className="size-1.5 rounded-full bg-accent p-live"
          />
          <span
            className={cx(
              "font-semibold",
              live.resting ? "text-accent-ink" : "text-ink"
            )}
          >
            {live.resting ? liveRest({ clock: live.clock }) : live.clock}
          </span>
          <span aria-hidden="true">·</span>
          {liveSets({ done: live.done, total: live.total })}
        </p>
      ) : (
        <IdleLine />
      )}
      <nav aria-label={navLabel()} className="mt-8 flex flex-col gap-1">
        {TABS.map((tab) => (
          <NavRow key={tab.id} tab={tab} />
        ))}
        <NavRow tab={COACH_TAB} />
      </nav>
      {/* The app's settings and the account (`D217`, `D218`). */}
      <button
        className="mt-auto flex min-h-12 items-center gap-3 rounded-xl px-3 text-left font-medium text-base text-ink-2 transition-colors hover:bg-surface-2"
        data-settings-door=""
        onClick={openSettings}
        type="button"
      >
        <Icon icon={Settings} size={20} />
        {settingsLabel()}
      </button>
    </aside>
  );
}
